import { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import AuthGuard from './components/AuthGuard';
import BlogLayout from './components/blog/BlogLayout';
import BrowseLayout from './components/BrowseLayout';
import DashboardLayout from './components/dashboard/DashboardLayout';
import DocsLayout from './components/docs/DocsLayout';
import GuestGuard from './components/GuestGuard';
import LoadingScreen from './components/LoadingScreen';
import MainLayout from './components/MainLayout';

const Loadable = (Component) => (props) => (
  <Suspense fallback={<LoadingScreen />}>
    <Component {...props} />
  </Suspense>
);

// Browse pages

const Browse = Loadable(lazy(() => import('./pages/browse/Browse')));
const BrowseButtons = Loadable(lazy(() => import('./pages/browse/BrowseButtons')));
const BrowseCharts = Loadable(lazy(() => import('./pages/browse/BrowseCharts')));
const BrowseColors = Loadable(lazy(() => import('./pages/browse/BrowseColors')));
const BrowseDetailLists = Loadable(lazy(() => import('./pages/browse/BrowseDetailLists')));
const BrowseForms = Loadable(lazy(() => import('./pages/browse/BrowseForms')));
const BrowseGridLists = Loadable(lazy(() => import('./pages/browse/BrowseGridLists')));
const BrowseGroupedLists = Loadable(lazy(() => import('./pages/browse/BrowseGroupedLists')));
const BrowseInputs = Loadable(lazy(() => import('./pages/browse/BrowseInputs')));
const BrowseModals = Loadable(lazy(() => import('./pages/browse/BrowseModals')));
const BrowseQuickStats = Loadable(lazy(() => import('./pages/browse/BrowseQuickStats')));
const BrowseTables = Loadable(lazy(() => import('./pages/browse/BrowseTables')));
const BrowseTypography = Loadable(lazy(() => import('./pages/browse/BrowseTypography')));

// Authentication pages

const Login = Loadable(lazy(() => import('./pages/authentication/Login')));
const PasswordRecovery = Loadable(lazy(() => import('./pages/authentication/PasswordRecovery')));
const PasswordReset = Loadable(lazy(() => import('./pages/authentication/PasswordReset')));
const Register = Loadable(lazy(() => import('./pages/authentication/Register')));
const VerifyCode = Loadable(lazy(() => import('./pages/authentication/VerifyCode')));

// Blog pages

const BlogPostCreate = Loadable(lazy(() => import('./pages/blog/BlogPostCreate')));
const BlogPostDetails = Loadable(lazy(() => import('./pages/blog/BlogPostDetails')));
const BlogPostList = Loadable(lazy(() => import('./pages/blog/BlogPostList')));

// Dashboard pages

const Account = Loadable(lazy(() => import('./pages/dashboard/Account')));
const Analytics = Loadable(lazy(() => import('./pages/dashboard/Analytics')));
const Calendar = Loadable(lazy(() => import('./pages/dashboard/Calendar')));
const Chat = Loadable(lazy(() => import('./pages/dashboard/Chat')));
const CustomerDetails = Loadable(lazy(() => import('./pages/dashboard/CustomerDetails')));
const CustomerEdit = Loadable(lazy(() => import('./pages/dashboard/CustomerEdit')));
const CustomerList = Loadable(lazy(() => import('./pages/dashboard/CustomerList')));
const Finance = Loadable(lazy(() => import('./pages/dashboard/Finance')));
const InvoiceDetails = Loadable(lazy(() => import('./pages/dashboard/InvoiceDetails')));
const InvoiceList = Loadable(lazy(() => import('./pages/dashboard/InvoiceList')));
const Kanban = Loadable(lazy(() => import('./pages/dashboard/Kanban')));
const Mail = Loadable(lazy(() => import('./pages/dashboard/Mail')));
const OrderDetails = Loadable(lazy(() => import('./pages/dashboard/OrderDetails')));
const OrderList = Loadable(lazy(() => import('./pages/dashboard/OrderList')));
const Overview = Loadable(lazy(() => import('./pages/dashboard/Overview')));
const ProductCreate = Loadable(lazy(() => import('./pages/dashboard/ProductCreate')));
const ProductList = Loadable(lazy(() => import('./pages/dashboard/ProductList')));

// Docs pages

const Docs = Loadable(lazy(() => import('./pages/Docs')));

// Error pages

const AuthorizationRequired = Loadable(lazy(() => import('./pages/AuthorizationRequired')));
const NotFound = Loadable(lazy(() => import('./pages/NotFound')));
const ServerError = Loadable(lazy(() => import('./pages/ServerError')));

// Projects pages

const ProjectBrowse = Loadable(lazy(() => import('./pages/dashboard/ProjectBrowse')));
const ProjectCreate = Loadable(lazy(() => import('./pages/dashboard/ProjectCreate')));
const ProjectDetails = Loadable(lazy(() => import('./pages/dashboard/ProjectDetails')));

// Social pages

const SocialFeed = Loadable(lazy(() => import('./pages/dashboard/SocialFeed')));
const SocialProfile = Loadable(lazy(() => import('./pages/dashboard/SocialProfile')));

// Other pages

const Checkout = Loadable(lazy(() => import('./pages/Checkout')));
const Contact = Loadable(lazy(() => import('./pages/Contact')));
// const Home = Loadable(lazy(() => import('./pages/Home')));
const Pricing = Loadable(lazy(() => import('./pages/Pricing')));

// Nuestras Vistas
const VehiclesList = Loadable(lazy(() => import('./views/dashboard/concessionaire/VehiclesList')));
const VehicleCreate = Loadable(lazy(() => import('./views/dashboard/concessionaire/VehicleCreate')));
const VehicleEdit = Loadable(lazy(() => import('./views/dashboard/concessionaire/VehicleEdit')));
const VehicleDetails = Loadable(lazy(() => import('./views/dashboard/concessionaire/VehicleDetails')));
const SucursalesList = Loadable(lazy(() => import('./views/dashboard/concessionaire/sucursales/SucursalesList')));
const AppointmentsList = Loadable(lazy(() => import('./views/dashboard/concessionaire/appointments/AppointmentsList')));
const AppointmentsDetails = Loadable(lazy(() => import('./views/dashboard/concessionaire/appointments/AppointmentsDetails')));

const ClientsList = Loadable(lazy(() => import('./views/dashboard/workshop/clients/ClientsList')));
const ClientCreate = Loadable(lazy(() => import('./views/dashboard/workshop/clients/ClientCreate')));
const ClientDetails = Loadable(lazy(() => import('./views/dashboard/workshop/clients/ClientDetails')));

const VehiclesWorkshopList = Loadable(lazy(() => import('./views/dashboard/workshop/vehicles/VehiclesWorkshopList')));
const VehicleWorkshopCreate = Loadable(lazy(() => import('./views/dashboard/workshop/vehicles/VehicleWorkshopCreate')));
const VehicleWorkshopDetails = Loadable(lazy(() => import('./views/dashboard/workshop/vehicles/VehicleWorkshopDetails')));

const OrdersList = Loadable(lazy(() => import('./views/dashboard/workshop/orders/OrdersList')));
const OrdersCreate = Loadable(lazy(() => import('./views/dashboard/workshop/orders/OrdersCreate')));
const OrderWorkshopDetails = Loadable(lazy(() => import('./views/dashboard/workshop/orders/OrderDetails')));
const InactiveOrderDetails = Loadable(lazy(() => import('./views/dashboard/workshop/orders/InactiveOrderDetails')));
const AppointmentsWorshopList = Loadable(lazy(() => import('./views/dashboard/workshop/appointments/AppointmentsList')));
const AppointmentsWorshopDetails = Loadable(lazy(() => import('./views/dashboard/workshop/appointments/AppointmentsDetails')));
const AppointmentsListAlliances = Loadable(lazy(() => import('./views/dashboard/alliances/appointmets/AppointmentsListAlliances')));
const AppointmentsDetailsAlliances = Loadable(lazy(() => import('./views/dashboard/alliances/appointmets/AppointmentsDetailsAlliances')));

const AdminList = Loadable(lazy(() => import('./views/dashboard/workshop/admins/AdminList')));
const AdminCreate = Loadable(lazy(() => import('./views/dashboard/workshop/admins/AdminCreate')));

const System = Loadable(lazy(() => import('./views/dashboard/system/System')));

const routes = [
  {
    path: 'authentication',
    children: [
      {
        path: 'login',
        element: (
          <GuestGuard>
            <Login />
          </GuestGuard>
        )
      },
      {
        path: 'login-unguarded',
        element: <Login />
      },
      {
        path: 'password-recovery',
        element: <PasswordRecovery />
      },
      {
        path: 'password-reset',
        element: <PasswordReset />
      },
      {
        path: 'register',
        element: (
          <GuestGuard>
            <Register />
          </GuestGuard>
        )
      },
      {
        path: 'register-unguarded',
        element: <Register />
      },
      {
        path: 'verify-code',
        element: <VerifyCode />
      }
    ]
  },
  {
    path: 'blog',
    element: <BlogLayout />,
    children: [
      {
        index: true,
        element: <BlogPostList />
      },
      {
        path: 'new',
        element: <BlogPostCreate />
      },
      {
        path: ':postId',
        element: <BlogPostDetails />
      }
    ]
  },
  {
    path: 'contact',
    element: <Contact />
  },
  {
    path: 'dashboard',
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <Overview />
      },
      {
        path: 'account',
        element: <Account />
      },
      {
        path: 'concesionario',
        children: [
          {
            path: 'vehiculos',
            element: <VehiclesList />
          },
          {
            path: 'vehiculos/crear',
            element: <VehicleCreate />
          },
          {
            path: 'vehiculos/editar/:id',
            element: <VehicleEdit />
          },
          {
            path: 'vehiculos/detalles/:id',
            element: <VehicleDetails />
          },
          {
            path: 'sucursales',
            element: <SucursalesList />
          },
          {
            path: 'citas',
            element: <AppointmentsList />
          },
          {
            path: 'citas/detalles/:id',
            element: <AppointmentsDetails />
          }
        ]
      },
      {
        path: 'alianzas',
        children: [
          {
            index: true,
            element: <AppointmentsListAlliances />
          },
          {
            path: 'detalles/:id',
            element: <AppointmentsDetailsAlliances />
          }
        ]
      },
      {
        path: 'taller',
        children: [
          {
            path: 'clientes',
            element: <ClientsList />
          },
          {
            path: 'clientes/crear',
            element: <ClientCreate />
          },
          {
            path: 'clientes/detalles/:id',
            element: <ClientDetails />
          },
          {
            path: 'vehiculos',
            element: <VehiclesWorkshopList />
          },
          {
            path: 'vehiculos/crear',
            element: <VehicleWorkshopCreate />
          },
          {
            path: 'vehiculos/detalles/:id',
            element: <VehicleWorkshopDetails />
          },
          {
            path: 'ordenes',
            element: <OrdersList />
          },
          {
            path: 'ordenes/crear',
            element: <OrdersCreate />
          },
          {
            path: 'ordenes/detalles/:id',
            element: <OrderWorkshopDetails />
          },
          {
            path: 'ordenes/inactivas/detalles/:id',
            element: <InactiveOrderDetails />
          },
          {
            path: 'citas',
            element: <AppointmentsWorshopList />
          },
          {
            path: 'citas/detalles/:id',
            element: <AppointmentsWorshopDetails />
          }
        ]
      },
      {
        path: 'admins',
        children: [
          {
            path: 'listado',
            element: <AdminList />
          },
          {
            path: 'crear',
            element: <AdminCreate />
          }
        ]
      },
      {
        path: 'sistema',
        element: <System />
      },
      {
        path: 'analytics',
        element: <Analytics />
      },
      {
        path: 'calendar',
        element: <Calendar />
      },
      {
        path: 'chat',
        children: [
          {
            index: true,
            element: <Chat />
          },
          {
            path: 'new',
            element: <Chat />
          },
          {
            path: ':threadKey',
            element: <Chat />
          }
        ]
      },
      {
        path: 'customers',
        children: [
          {
            index: true,
            element: <CustomerList />
          },
          {
            path: ':customerId',
            element: <CustomerDetails />
          },
          {
            path: ':customerId/edit',
            element: <CustomerEdit />
          }
        ]
      },
      {
        path: 'invoices',
        children: [
          {
            index: true,
            element: <InvoiceList />
          },
          {
            path: ':invoiceId',
            element: <InvoiceDetails />
          }
        ]
      },
      {
        path: 'kanban',
        element: <Kanban />
      },
      {
        path: 'mail',
        children: [
          {
            index: true,
            element: (
              <Navigate
                to="/dashboard/mail/all"
                replace
              />
            )
          },
          {
            path: 'label/:customLabel',
            element: <Mail />
          },
          {
            path: 'label/:customLabel/:emailId',
            element: <Mail />
          },
          {
            path: ':systemLabel',
            element: <Mail />
          },
          {
            path: ':systemLabel/:emailId',
            element: <Mail />
          }
        ]
      },
      {
        path: 'orders',
        children: [
          {
            index: true,
            element: <OrderList />
          },
          {
            path: ':orderId',
            element: <OrderDetails />
          }
        ]
      },
      {
        path: 'finance',
        element: <Finance />
      },
      {
        path: 'products',
        children: [
          {
            index: true,
            element: <ProductList />
          },
          {
            path: 'new',
            element: <ProductCreate />
          }
        ]
      },
      {
        path: 'projects',
        children: [
          {
            path: 'browse',
            element: <ProjectBrowse />
          },
          {
            path: 'new',
            element: <ProjectCreate />
          },
          {
            path: ':projectId',
            element: <ProjectDetails />
          }
        ]
      },
      {
        path: 'social',
        children: [
          {
            path: 'feed',
            element: <SocialFeed />
          },
          {
            path: 'profile',
            element: <SocialProfile />
          }
        ]
      }
    ]
  },
  {
    path: 'docs',
    element: <DocsLayout />,
    children: [
      {
        index: true,
        element: (
          <Navigate
            to="/docs/overview/welcome"
            replace
          />
        )
      },
      {
        path: '*',
        element: <Docs />
      }
    ]
  },
  {
    path: '*',
    element: <MainLayout />,
    children: [
      {
        index: true,
        element:
          (
            <GuestGuard>
              <Login />
            </GuestGuard>
          )
      },
      {
        path: 'browse',
        element: <BrowseLayout />,
        children: [
          {
            index: true,
            element: <Browse />
          },
          {
            path: 'buttons',
            element: <BrowseButtons />
          },
          {
            path: 'inputs',
            element: <BrowseInputs />
          },
          {
            path: 'charts',
            element: <BrowseCharts />
          },
          {
            path: 'colors',
            element: <BrowseColors />
          },
          {
            path: 'data-display/detail-lists',
            element: <BrowseDetailLists />
          },
          {
            path: 'data-display/quick-stats',
            element: <BrowseQuickStats />
          },
          {
            path: 'data-display/tables',
            element: <BrowseTables />
          },
          {
            path: 'forms',
            element: <BrowseForms />
          },
          {
            path: 'modals',
            element: <BrowseModals />
          },
          {
            path: 'lists/grouped-lists',
            element: <BrowseGroupedLists />
          },
          {
            path: 'lists/grid-lists',
            element: <BrowseGridLists />
          },
          {
            path: 'typography',
            element: <BrowseTypography />
          }
        ]
      },
      {
        path: 'checkout',
        element: <Checkout />
      },
      {
        path: 'pricing',
        element: <Pricing />
      },
      {
        path: '401',
        element: <AuthorizationRequired />
      },
      {
        path: '404',
        element: <NotFound />
      },
      {
        path: '500',
        element: <ServerError />
      },
      {
        path: '*',
        element: <NotFound />
      }
    ]
  }
];

export default routes;
